import React from 'react';
import {
  Slider, Handles, Tracks, Rail
} from 'react-compound-slider';
import PropTypes from 'prop-types';
import classes from '../classes.module.scss';
import Handle from './filter_handle';
import Track from './slider_track';
import withPGTranslation from '../../../../../config/withPGTranslation';
import {
  formatFilterAmount,
  getCurrencySignForCode,
  getCurrentCurrencyCode
} from '../../../../../config/util';

const getLocalCurrencyDisplayParts = (amount, currencyCode) => {
  const code = (currencyCode != null && currencyCode !== '') ? currencyCode : getCurrentCurrencyCode();
  const currencySign = getCurrencySignForCode(code);
  const sign = parseFloat(amount) < 0 ? `-${ currencySign }` : currencySign;
  return { sign, amount: formatFilterAmount(amount) };
};

const renderSliderLimitPrice = (amount, position, currencyCode) => {
  const { sign, amount: amountPart } = getLocalCurrencyDisplayParts(amount, currencyCode);
  const positionClass = position === 'max'
    ? classes.sliderLimitPriceLabelEnd
    : classes.sliderLimitPriceLabelStart;

  return (
    <span className={ `${ classes.sliderLimitPriceLabel } ${ positionClass }` }>
      <span className={ classes.sliderLimitPriceSign }>{ sign }</span>
      <span className={ classes.sliderLimitPriceAmount }>{ amountPart }</span>
    </span>
  );
};

// Handles may land on the same price but never pass each other — crossing re-keys them by
// index, handing the drag to the neighbour and walking both handles along together.
const allowEqualPricesMode = (curr, next) => {
  const crossed = next.some(({ key, val }, i) => key !== curr[i].key || (i > 0 && val < next[i - 1].val));
  return crossed ? curr : next;
};

const renderSliderPrice = (amount, currencyCode) => {
  const { sign, amount: amountPart } = getLocalCurrencyDisplayParts(amount, currencyCode);
  return `${ sign }${ amountPart }`;
};

function FilterSlider({
  currentPPMRange, onRangeChange, t, currency: currencyCode, localFilterPricePerMinute
}) {
  const { min, max, step } = localFilterPricePerMinute || {};
  const priceValues = () => (
    <div className={ classes.sliderPrices }>
      { renderSliderLimitPrice(min, 'min', currencyCode) }
      <span className={ classes.selectedPriceContainer }>
        <span className={ classes.selectedPriceRangeStart }>
          {`${ renderSliderPrice(currentPPMRange[0], currencyCode) } -${ '\u00A0' }`}
        </span>
        <span className={ classes.selectedPriceRangeEnd }>
          {`${ renderSliderPrice(currentPPMRange[1], currencyCode) }/min`}
        </span>
      </span>
      { renderSliderLimitPrice(max, 'max', currencyCode) }
    </div>
  );

  return (
    <div className={ classes.itemContainer } data-testid="filter-slider">
      <div className={ classes.itemHolder }>
        <span className={ classes.itemTitle }>
          { t('home_page.price_per_minute') }
        </span>
        <div className={ classes.sliderContainer }>
          <Slider
            className={ classes.sliderRoot }
            domain={ [min, max] }
            step={ step }
            values={ currentPPMRange }
            mode={ allowEqualPricesMode }
            onUpdate={ onRangeChange }
          >
            <Rail>
              {({ getRailProps }) => (
                <div className={ classes.rail } { ...getRailProps() } />
              )}
            </Rail>
            <Handles>
              {({ handles, getHandleProps }) => (
                <div>
                  {handles.map((handle, index) => (
                    <Handle
                      key={ handle.id }
                      handle={ handle }
                      getHandleProps={ getHandleProps }
                      isLeft={ index === 0 }
                    />
                  ))}
                </div>
              )}
            </Handles>
            <Tracks right={ false } left={ false }>
              {({ tracks, getTrackProps }) => (
                <div>
                  {tracks.map(({ id, source, target }) => (
                    <Track
                      key={ id }
                      source={ source }
                      target={ target }
                      getTrackProps={ getTrackProps }
                    />
                  ))}
                </div>
              )}
            </Tracks>
          </Slider>
          {priceValues()}
        </div>
      </div>
    </div>
  );
}

FilterSlider.propTypes = {
  currentPPMRange: PropTypes.array.isRequired,
  onRangeChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  currency: PropTypes.string,
  localFilterPricePerMinute: PropTypes.object
};

FilterSlider.defaultProps = {
  currency: undefined,
  localFilterPricePerMinute: PropTypes.object
};

export default withPGTranslation(FilterSlider);
